import { useContext } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import Button from '@base/Button/Button';
import { Spacings } from '../../../styleConstants';

// Inner container should be 464px = (560 - (48 + 48)) where 48px is padding
const MsgWrapper = styled.div`
  width: 560px;
  padding: ${Spacings.SPACING_10B} ${Spacings.SPACING_12B};
  text-align: center;
  @media (max-width: 47.95em) {
    width: 100%;
    padding: 16px;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  button {
    &:not(:first-child) {
      margin-left: ${Spacings.SPACING_4B};
    }
  }
`;

const StyP1 = styled(P1)`
  color: ${({ theme }) => theme.colors.fontGrey};
  width: ${({ $width }) => $width};
  margin: ${Spacings.SPACING_2B} auto ${Spacings.SPACING_8B} auto;
`;

const DialogMessage = ({
  type,
  heading,
  message,
  messageWidth,
  acceptLabel,
  acceptLabelVariant,
  acceptLabelTheme,
  rejectLabel,
  acceptOnClick,
  rejectOnClick,
  showButtons,
  children
}) => {
  const theme = useContext(ThemeContext);

  const getRejectButton = () => (
    <Button
      label={rejectLabel}
      onClick={rejectOnClick}
      size="small"
      variant="hollow"
      theme="black"
    />
  );

  const getDefaultButton = () => (
    <Button
      label={acceptLabel}
      onClick={acceptOnClick}
      size="small"
      variant={acceptLabelVariant}
      theme={acceptLabelTheme}
    />
  );

  const getButtons = () => {
    if (type === 'default') {
      return (
        <>
          {rejectLabel && rejectOnClick && getRejectButton()}
          {acceptLabel && acceptOnClick && getDefaultButton()}
        </>
      );
    }
    if (type === 'destructive') {
      return (
        <>
          {acceptLabel && acceptOnClick && getDefaultButton()}
          {rejectLabel && rejectOnClick && getRejectButton()}
        </>
      );
    }
    if (type === 'informative') {
      return getDefaultButton();
    }
  };
  return (
    <MsgWrapper>
      <H3 color={theme.colors.fontGrey}>{heading}</H3>
      {message && (
        <StyP1 color={theme.colors.fontLightGrey} $width={messageWidth}>
          {message}
        </StyP1>
      )}
      {children}
      {showButtons && <ButtonsWrapper>{getButtons()}</ButtonsWrapper>}
    </MsgWrapper>
  );
};
export default DialogMessage;
DialogMessage.propTypes = {
  type: PropTypes.oneOf(['default', 'destructive', 'informative']),
  heading: PropTypes.string.isRequired,
  message: PropTypes.string,
  messageWidth: PropTypes.string,
  acceptLabel: PropTypes.string,
  acceptLabelVariant: PropTypes.oneOf(['hollow', 'filled']),
  acceptLabelTheme: PropTypes.oneOf(['red', 'green', 'blue']),
  rejectLabel: PropTypes.string,
  acceptOnClick: PropTypes.func,
  rejectOnClick: PropTypes.func,
  showButtons: PropTypes.bool,
  children: PropTypes.node
};
DialogMessage.defaultProps = {
  type: 'destructive',
  showButtons: true,
  children: null,
  message: null,
  messageWidth: '400px',
  acceptLabel: null,
  acceptLabelVariant: 'hollow',
  acceptLabelTheme: 'red',
  rejectLabel: null,
  acceptOnClick: null,
  rejectOnClick: null
};
